import { emptyObject } from 'ui-components/default-props/empty';

import { useSellerWallets } from '../../hooks/use-seller-wallets';
import { LogService } from '../../services/log-service';

/**
 * @typedef {import('../../prop-types/currency')} Currency
 */

/**
 * @typedef {object} WalletAutoFundConfig
 * @property {Currency} amount - the amount to fund in each autofunding event
 * @property {number} cutoff - the number of autofunding events allowed to occur in one day
 * @property {Currency} threshold - the wallet balance threshold that triggers an autofunding event
 */

/**
 * @typedef {object} WalletResources
 * @property {string[]} sellerLabelProviderIds - the ids of the seller label providers that belong to this wallet
 * @property {string[]} insuranceProviderIds - the ids of the activated insurance providers that belong to this wallet
 */

/**
 * @typedef {object} Wallet
 * // Identifiers
 * @property {string} walletId - the id of the wallet
 * @property {string} currencyCode - the ISO 4217 currency code of the wallet
 * @property {string} nickname - the user-given nickname of the wallet
 * @property {string} brandName - the user-facing branding for the wallet
 * // Wallet State
 * @property {Currency} balance - the current balance of the wallet
 * @property {WalletAutoFundConfig} autoFundConfig - the configuration for the wallet's autofunding
 * @property {Currency} defaultPurchaseConfig - the default amount to fund the wallet when not using autofunding
 * // Wallet resource state
 * @property {boolean} isSetupComplete - (deprecated) Use isWalletRegistered instead
 * @property {boolean} isWalletRegistered - whether a user registered the wallet (i.e. can be used for purchases)
 * @property {boolean} hasNewResources - whether the wallet has new resources available
 * @property {boolean} hasWalletUpdate - whether the wallet is registered AND has new resources available
 * @property {WalletResources} resources - the activated resources of the wallet that are ready to use
 * @property {string[]} newInsuranceProviderIds - the ids of insurance providers that can be added to the wallet
 * @property {string[]} newLabelProviderIds - the ids of label providers that can be added to the wallet
 * @property {string[]} newResourceUpdates - the ids of resources that can be updated
 */

/**
 * Returns the wallet specified by the `walletId` parameter, or the first wallet if not specified. If the specified
 * wallet is not found, the first wallet is returned.
 * @param {string} [walletId] Optional string to specify which wallet to get.
 * @param {boolean} [requireExactWallet] Our app does not support multiple wallets; however, much of our code was
 *    written to support multiple. In almost all cases, a caller to this hook should not care about which wallet is
 *    returned, moreso that the seller's singular is returned. Because of this, the default behavior is to return the
 *    first wallet if the specified wallet is not found. In the rare case that this could be a problem, the caller can
 *    set this flag to true to require that the only specified wallet is returned. This will return an empty object if
 *    the wallet is not found.
 * Optional boolean to specify if another wallet can be returned if the specified wallet is not found
 * @returns {Wallet} combination of wallet and wallet eligibility data
 */
export const useWallet = (walletId, requireExactWallet = false) => {
  const wallets = useSellerWallets();

  if (walletId) {
    const targetWallet = wallets.find((_wallet) => _wallet.walletId === walletId);
    if (!targetWallet) {
      LogService.logInfo(`useWallet hook: Wallet with id ${walletId} not found`);
      return requireExactWallet ? emptyObject : (wallets[0] ?? emptyObject);
    }
    return targetWallet;
  }
  return wallets.length > 0 ? wallets[wallets.length - 1] : emptyObject;
};
